import { fileEntrySchema, hasPermission } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, FileCode, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { LazyCodeEditor } from '@/components/lazy-code-editor';
import { QueryState } from '@/components/query-state';
import { StatusPill } from '@/components/status-badges';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiRequest, ApiRequestError } from '@/lib/api';
import { baseName, languageFor, normalizeDir, parentOf, type EditorLanguage } from '@/lib/files';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/edit')({
  validateSearch: (s: Record<string, unknown>): { file: string; new?: boolean } => ({
    file: normalizeDir(typeof s.file === 'string' ? s.file : '/'),
    ...(s.new === true || s.new === 'true' ? { new: true } : {}),
  }),
  component: EditPage,
});

const LANGUAGES: EditorLanguage[] = [
  'plaintext',
  'ini',
  'yaml',
  'json',
  'shell',
  'xml',
  'javascript',
  'python',
  'lua',
  'markdown',
  'html',
  'css',
  'sql',
  'dockerfile',
];

function EditPage() {
  const { server: id } = Route.useParams();
  const { file, new: isNew } = Route.useSearch();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [created, setCreated] = useState(false);
  const creating = Boolean(isNew) && !created;

  const contents = useQuery({
    queryKey: ['server', id, 'file', file],
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${id}/files/contents?file=${encodeURIComponent(file)}`, {
        schema: z.string(),
        signal,
        textResponse: true,
      }),
    enabled: !isNew,
    gcTime: 0,
  });
  const [value, setValue] = useState<string | null>(isNew ? '' : null);
  const [language, setLanguage] = useState<EditorLanguage>(languageFor(file));
  useEffect(() => {
    if (contents.data !== undefined && value === null) setValue(contents.data);
  }, [contents.data, value]);
  const dirty = value !== null && value !== (isNew ? '' : contents.data);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const canWrite = hasPermission(server.permissions, creating ? 'file.create' : 'file.update');
  const save = useMutation({
    mutationFn: (text: string) =>
      apiRequest(
        `/api/client/servers/${id}/files/write?file=${encodeURIComponent(file)}&mode=${creating ? 'create' : 'update'}`,
        { method: 'POST', body: text, textBody: true, schema: fileEntrySchema },
      ),
    onSuccess: async (entry, text) => {
      queryClient.setQueryData(['server', id, 'file', file], text);
      await queryClient.invalidateQueries({ queryKey: ['server', id, 'files'] });
      if (creating) setCreated(true);
      toast.success(`Saved ${entry.name}.`);
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiRequestError && e.body?.code === 'exists'
          ? 'A file with that name already exists.'
          : errorMessage(e),
      ),
  });
  const doSave = () => {
    if (value !== null && canWrite && !save.isPending) save.mutate(value);
  };
  const dir = parentOf(file);

  return (
    <div className="grid gap-3">
      <div className="bg-card shadow-card overflow-hidden rounded-xl border">
        <div className="bg-elevated/40 flex flex-wrap items-center justify-between gap-3 border-b px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <Button asChild size="icon-sm" variant="ghost" aria-label="Back to files">
              <Link
                to="/server/$server/files"
                params={{ server: id }}
                search={dir === '/' ? {} : { dir }}
              >
                <ArrowLeft />
              </Link>
            </Button>
            <FileCode className="text-subtle size-4 shrink-0" aria-hidden />
            <h2 className="min-w-0 truncate font-mono text-sm" title={file}>
              <span className="text-muted-foreground">{dir === '/' ? '' : dir}/</span>
              <span className="font-semibold">{baseName(file)}</span>
            </h2>
            {!canWrite ? (
              <StatusPill tone="muted">Read-only</StatusPill>
            ) : save.isPending ? (
              <StatusPill tone="info" pulse>
                Saving
              </StatusPill>
            ) : dirty || creating ? (
              <StatusPill tone="warning">{creating ? 'New file' : 'Unsaved changes'}</StatusPill>
            ) : (
              <StatusPill tone="success">Saved</StatusPill>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select value={language} onValueChange={(v) => setLanguage(v as EditorLanguage)}>
              <SelectTrigger size="sm" className="w-40" aria-label="Syntax highlighting">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l === 'plaintext' ? 'Plain text' : l === 'ini' ? 'Properties / INI' : l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={doSave}
              disabled={!canWrite || value === null || save.isPending || (!dirty && !creating)}
            >
              {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}{' '}
              {creating ? 'Create' : 'Save'}
            </Button>
          </div>
        </div>
        {isNew ? (
          <Editor
            value={value ?? ''}
            onChange={setValue}
            language={language}
            onSave={doSave}
            readOnly={!canWrite}
          />
        ) : (
          <QueryState query={contents}>
            {() => (
              <Editor
                value={value ?? ''}
                onChange={setValue}
                language={language}
                onSave={doSave}
                readOnly={!canWrite}
              />
            )}
          </QueryState>
        )}
      </div>
      <p className="text-muted-foreground text-xs">
        {canWrite ? 'Press ⌘S / Ctrl+S to save.' : 'You can read this file but not change it.'}{' '}
        <button
          type="button"
          className="underline"
          onClick={() =>
            void navigate({
              to: '/server/$server/files',
              params: { server: id },
              search: dir === '/' ? {} : { dir },
            })
          }
        >
          Back to files
        </button>
      </p>
    </div>
  );
}

function Editor(props: {
  value: string;
  onChange: (v: string) => void;
  language: EditorLanguage;
  onSave: () => void;
  readOnly: boolean;
}) {
  return <LazyCodeEditor {...props} height="calc(100dvh - 24rem)" ariaLabel="File contents" bare />;
}
